import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  importDriveFiles,
  type DriveImportFile,
  type DriveImportOptions,
  type DriveImportResult,
} from "@/services/googleDriveImportService";
import { useKnowledgeBaseList } from "@/contexts/KnowledgeBaseContext";

export type ImportMutationInput = {
  files: DriveImportFile[];
  options: Omit<DriveImportOptions, "token" | "kbItems"> & { token: string };
};

export function useGoogleDriveImportMutations() {
  const queryClient = useQueryClient();
  const { items: kbItems } = useKnowledgeBaseList();

  const invalidateAfterImport = () => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        return (
          typeof key === "string" &&
          (key === "/api/knowledge-base" ||
            key === "/api/knowledgebase" ||
            key === "knowledgebase" ||
            key === "knowledgebase-summary" ||
            key === "embedding-status" ||
            key === "/api/library-folders")
        );
      },
    });
    queryClient.invalidateQueries({ queryKey: ["google-drive"] });
  };

  const importMutation = useMutation({
    mutationFn: async ({
      files,
      options,
    }: ImportMutationInput): Promise<DriveImportResult> => {
      return importDriveFiles(files, {
        ...options,
        kbItems,
        updateExisting: options.updateExisting ?? false,
      });
    },
    onSuccess: invalidateAfterImport,
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      files,
      options,
    }: ImportMutationInput): Promise<DriveImportResult> => {
      return importDriveFiles(files, {
        ...options,
        kbItems,
        updateExisting: true,
      });
    },
    onSuccess: invalidateAfterImport,
  });

  return { importMutation, updateMutation };
}
